import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";

import "../styles/AdminLinks.css";
import { LINKS_API } from "../config/api";
import { showConfirm } from "../utils/customDialog";

function AdminLinks() {
  const [videos, setVideos] = useState([]);
  const [activeVideo, setActiveVideo] = useState("");
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("watch-videos");

  const [form, setForm] = useState({
    video_id: "",
    title: "",
    description: "",
  });

  const popupRef = useRef(null);

  /* ================= POPUP ================= */
  const showPopup = useCallback(
    (message, type = "success") => {
      if (popupRef.current) {
        popupRef.current.remove();
      }

      const popup = document.createElement("div");

      popup.className = `admin-popup ${type}`;

      popup.innerHTML = `
        <div class="popup-content">
          ${message}
        </div>
      `;

      popupRef.current = popup;

      document.body.appendChild(popup);

      setTimeout(() => {
        popup.classList.add("hide");
      }, 2200);

      setTimeout(() => {
        popup.remove();
        popupRef.current = null;
      }, 2800);
    },
    []
  );

  /* ================= EXTRACT VIDEO ID ================= */
  const extractVideoId = (input) => {
    if (!input) return "";

    const trimmed = input.trim();

    // direct video id
    if (
      trimmed.length === 11 &&
      !trimmed.includes("http")
    ) {
      return trimmed;
    }

    const match = trimmed.match(
      /(?:youtube\.com.*v=|youtu\.be\/|youtube\.com\/embed\/)([^&?/]+)/
    );

    return match ? match[1] : trimmed;
  };

  /* ================= FETCH VIDEOS ================= */
  const fetchVideos = useCallback(async () => {
    try {
      setLoading(true);

      const res = await fetch(LINKS_API);

      const data = await res.json();

      const safeVideos = Array.isArray(data)
        ? data
        : [];

      setVideos(safeVideos);

      if (
        safeVideos.length > 0 &&
        !activeVideo
      ) {
        setActiveVideo(
          safeVideos[0].video_id
        );
      }
    } catch (err) {
      console.error("Fetch error:", err);

      showPopup(
        "Failed to load videos",
        "error"
      );

      setVideos([]);
    } finally {
      setLoading(false);
    }
  }, [activeVideo, showPopup]);

  // ================= UNLINK OTHER ADMIN CSS =================
  

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  /* ================= INPUT CHANGE ================= */
  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /* ================= RESET ================= */
  const resetForm = () => {
    setForm({
      video_id: "",
      title: "",
      description: "",
    });
  };

  /* ================= EDIT VIDEO ================= */
  const handleEditVideo = (video) => {
    setForm({
      video_id: video.video_id || "",
      title: video.title || "",
      description: video.description || "",
    });
    showPopup(`Editing Video: ${video.title}`);
    setActiveTab("manage-videos");
  };

  /* ================= VALIDATION ================= */
  const validateForm = () => {
    if (!form.video_id.trim()) {
      showPopup(
        "Video link is required",
        "error"
      );

      return false;
    }

    if (!form.title.trim()) {
      showPopup(
        "Video title is required",
        "error"
      );

      return false;
    }

    return true;
  };

  /* ================= ADD VIDEO ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      const video_id = extractVideoId(
        form.video_id
      );

      const res = await fetch(LINKS_API, {
        method: "POST",

        headers: {
          "Content-Type":
            "application/json",
        },

        body: JSON.stringify({
          video_id,
          title: form.title,
          description:
            form.description,
        }),
      });

      const result = await res.json();

      if (result?.success || result) {
        showPopup(
          "Video added successfully"
        );

        resetForm();

        fetchVideos();
        setActiveTab("watch-videos");
      } else {
        showPopup(
          "Failed to add video",
          "error"
        );
      }
    } catch (err) {
      console.error("Add error:", err);

      showPopup(
        "Server error occurred",
        "error"
      );
    }
  };

  /* ================= DELETE ================= */
  const handleDelete = async (id) => {
    const ok = await showConfirm({
      title: "Delete Video",
      message: "Are you sure you want to delete this video?",
      subMessage: "This will remove the video link from the public Videos section.",
      confirmText: "Delete Video",
      type: "danger"
    });
    if (!ok) return;

    try {
      const res = await fetch(LINKS_API, {
        method: "DELETE",

        headers: {
          "Content-Type":
            "application/json",
        },

        body: JSON.stringify({
          id,
        }),
      });

      const result = await res.json();

      if (result?.success || result) {
        showPopup(
          "Video deleted successfully"
        );

        setVideos((prev) =>
          prev.filter(
            (item) => item.id !== id
          )
        );

        // update active video safely
        if (
          videos.length > 1 &&
          activeVideo ===
            videos.find(
              (v) => v.id === id
            )?.video_id
        ) {
          const remaining =
            videos.filter(
              (v) => v.id !== id
            );

          if (remaining.length > 0) {
            setActiveVideo(
              remaining[0].video_id
            );
          }
        }
      } else {
        showPopup(
          "Delete failed",
          "error"
        );
      }
    } catch (err) {
      console.error(
        "Delete error:",
        err
      );

      showPopup(
        "Server error occurred",
        "error"
      );
    }
  };

  return (
    <div className="spiritual-container links-admin-container">
      {/* Header with Dashboard Metrics */}
      <div className="links-dashboard-header">
        <div className="header-title-area">
          <h1>
            <i className="bi bi-play-btn-fill"></i> Video Library & Broadcasts
          </h1>
          <p>Paste and manage spiritual videos, watch broadcasts, and configure titles</p>
        </div>

        <div className="links-metrics-grid">
          <div className="metric-card" onClick={() => setActiveTab("watch-videos")} style={{ cursor: "pointer" }}>
            <div className="metric-icon total-videos"><i className="bi bi-collection-play-fill"></i></div>
            <div className="metric-info">
              <span className="metric-value">{videos.length}</span>
              <span className="metric-label">Total Videos</span>
            </div>
          </div>
          <div className="metric-card">
            <div className="metric-icon active-video-title"><i className="bi bi-broadcast"></i></div>
            <div className="metric-info">
              <span className="metric-value" style={{ fontSize: "14px", fontWeight: "750" }}>
                {videos.find((v) => v.video_id === activeVideo)?.title || "None Selected"}
              </span>
              <span className="metric-label">Now Playing</span>
            </div>
          </div>
        </div>
      </div>

      {/* PAGE TABS NAVIGATION */}
      <div className="links-tabs">
        <button
          type="button"
          className={`links-tab-btn ${activeTab === "watch-videos" ? "active" : ""}`}
          onClick={() => setActiveTab("watch-videos")}
        >
          <i className="bi bi-tv-fill"></i>
          <span className="tab-text-container">
            <strong>Watch Broadcasts</strong>
            <span className="tab-subtext">View active player & sermons</span>
          </span>
        </button>

        <button
          type="button"
          className={`links-tab-btn ${activeTab === "manage-videos" ? "active" : ""}`}
          onClick={() => setActiveTab("manage-videos")}
        >
          <i className="bi bi-plus-circle"></i>
          <span className="tab-text-container">
            <strong>Upload & Manage</strong>
            <span className="tab-subtext">Add new videos & catalog</span>
          </span>
        </button>
      </div>

      {/* ================= WATCH VIDEOS TAB ================= */}
      {activeTab === "watch-videos" && (
        <div className="watch-tab-layout-grid">
          {/* Column 1: Video Player */}
          <div className="player-column">
            <section className="admin-section player-section">
              <div className="section-title">
                <h2>
                  <i className="bi bi-youtube"></i> Broadcast Player
                </h2>
                <p>
                  Now Playing: {videos.find((v) => v.video_id === activeVideo)?.title || "Select a video from the library"}
                </p>
              </div>

              <div className="video-player-wrapper">
                {activeVideo ? (
                  <iframe
                    width="100%"
                    height="450"
                    src={`https://www.youtube.com/embed/${activeVideo}`}
                    title="Spiritual Video"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                ) : (
                  <div className="empty-player">
                    <i className="bi bi-play-circle-fill"></i>
                    <p>No video selected. Select a sermon from the library list on the right to start watching!</p>
                  </div>
                )}
              </div>

              {activeVideo && (
                <div className="player-details">
                  <h3>{videos.find((v) => v.video_id === activeVideo)?.title}</h3>
                  <p>{videos.find((v) => v.video_id === activeVideo)?.description || "No description provided for this broadcast."}</p>
                </div>
              )}
            </section>
          </div>

          {/* Column 2: Play List */}
          <div className="playlist-column">
            <section className="admin-section playlist-section">
              <div className="section-header-area">
                <div className="title-info">
                  <h2><i className="bi bi-collection-play-fill"></i> Broadcast Library</h2>
                  <p>Click on any sermon to load it into the main player</p>
                </div>
              </div>

              <div className="playlist-list-wrapper">
                {loading ? (
                  <div className="loading-box">
                    <div className="spinner"></div>
                    <p>Loading videos...</p>
                  </div>
                ) : videos.length === 0 ? (
                  <div className="empty-box">
                    <i className="bi bi-play-circle"></i>
                    <p>No videos found</p>
                  </div>
                ) : (
                  <div className="playlist-items-list">
                    {videos.map((video) => (
                      <div
                        key={video.id}
                        className={`playlist-item-row ${activeVideo === video.video_id ? "active" : ""}`}
                        onClick={() => setActiveVideo(video.video_id)}
                      >
                        <div className="row-play-indicator">
                          {activeVideo === video.video_id ? (
                            <i className="bi bi-broadcast"></i>
                          ) : (
                            <i className="bi bi-play-circle-fill"></i>
                          )}
                        </div>
                        <div className="row-info-block">
                          <span className="video-title-span">{video.title}</span>
                          <span className="video-desc-span">
                            {video.description ? (
                              video.description.substring(0, 75) + (video.description.length > 75 ? "..." : "")
                            ) : (
                              "No description available"
                            )}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>
          </div>
        </div>
      )}

      {/* ================= UPLOAD & MANAGE TAB ================= */}
      {activeTab === "manage-videos" && (
        <div className="manage-tab-layout-grid">
          {/* Column 1: Add New Video Form */}
          <div className="form-column">
            <section className="admin-section form-section">
              <div className="section-title">
                <h2><i className="bi bi-plus-circle-fill"></i> Add New Broadcast</h2>
                <p>Paste a YouTube URL or direct Video ID to publish a sermon video</p>
              </div>

              <form className="link-form" onSubmit={handleSubmit}>
                <label className="field-label">YouTube URL or Video ID:</label>
                <input
                  type="text"
                  name="video_id"
                  placeholder="Paste YouTube Link (e.g. https://www.youtube.com/watch?v=...) or ID"
                  value={form.video_id}
                  onChange={handleChange}
                />

                <label className="field-label" style={{ marginTop: "16px" }}>Broadcast Title:</label>
                <input
                  type="text"
                  name="title"
                  placeholder="Enter a descriptive video title..."
                  value={form.title}
                  onChange={handleChange}
                />

                <label className="field-label" style={{ marginTop: "16px" }}>Broadcast Description:</label>
                <textarea
                  name="description"
                  placeholder="Enter video summary or sermon notes..."
                  value={form.description}
                  onChange={handleChange}
                  rows={4}
                />

                <div className="form-buttons">
                  <button className="submit-btn" type="submit">
                    Publish Video
                  </button>
                  <button type="button" className="reset-btn" onClick={resetForm}>
                    Reset Fields
                  </button>
                </div>
              </form>
            </section>
          </div>

          {/* Column 2: Video Catalog List with Delete */}
          <div className="catalog-column">
            <section className="admin-section catalog-section">
              <div className="section-title">
                <h2><i className="bi bi-trash-fill"></i> Delete & Manage Videos</h2>
                <p>View published videos, preview them in place, or remove them permanently</p>
              </div>

              <div className="catalog-list-wrapper">
                {loading ? (
                  <div className="loading-box">
                    <div className="spinner"></div>
                    <p>Loading video list...</p>
                  </div>
                ) : videos.length === 0 ? (
                  <div className="empty-box">
                    <i className="bi bi-trash"></i>
                    <p>No videos available to manage</p>
                  </div>
                ) : (
                  <div className="catalog-items-list">
                    {videos.map((video) => (
                      <div key={video.id} className="catalog-item-row">
                        <div className="catalog-info">
                          <span className="title-text">{video.title}</span>
                          <span className="desc-text">{video.video_id}</span>
                        </div>
                        <div className="catalog-actions">
                          <button
                            type="button"
                            className="edit-row-btn"
                            onClick={() => handleEditVideo(video)}
                            title="Edit Video Details"
                            style={{
                              background: "#eff6ff",
                              color: "#2563eb",
                              border: "1px solid #dbeafe",
                              borderRadius: "8px",
                              width: "32px",
                              height: "32px",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                              marginRight: "6px"
                            }}
                          >
                            <i className="bi bi-pencil-fill"></i>
                          </button>
                          <button
                            type="button"
                            className="preview-row-btn"
                            onClick={() => {
                              setActiveVideo(video.video_id);
                              setActiveTab("watch-videos");
                            }}
                            title="Preview in Player"
                          >
                            <i className="bi bi-eye-fill"></i>
                          </button>
                          <button
                            type="button"
                            className="delete-row-btn"
                            onClick={() => handleDelete(video.id)}
                            title="Delete Video"
                          >
                            <i className="bi bi-trash-fill"></i>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminLinks;
